import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Image as ImageIcon, X, Film, Loader2 } from "lucide-react";

/**
 * MediaPicker — mobile-first image/video attach for post composer.
 * - Uses native iOS camera roll picker + optional direct camera capture
 * - 16px input font to prevent iOS Safari zoom
 * - Compresses images >2MB client-side to keep uploads snappy on cellular
 */
export default function MediaPicker({ media, setMedia, max = 4, testIdPrefix = "composer" }) {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const canAdd = media.length < max;

  const compressIfLarge = (file) => new Promise((resolve) => {
    // Only compress images >2MB. Videos & small images pass through.
    if (!file.type.startsWith("image/") || file.size < 2 * 1024 * 1024) return resolve(file);
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const maxDim = 1920;
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const scale = maxDim / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(url);
          if (!blob) return resolve(file);
          resolve(new File([blob], file.name.replace(/\.(heic|heif)$/i, ".jpg"), { type: "image/jpeg" }));
        },
        "image/jpeg",
        0.85,
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });

  const handleFiles = async (fileList) => {
    if (!fileList || !fileList.length) return;
    const files = Array.from(fileList).slice(0, max - media.length);
    setUploading(true);
    try {
      for (const raw of files) {
        const file = await compressIfLarge(raw);
        const form = new FormData();
        form.append("file", file);
        form.append("kind", "social");
        const r = await api.post("/files/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
        const kind = file.type.startsWith("video/") ? "video" : "image";
        setMedia((prev) => [
          ...prev,
          { file_id: r.data.id, url: r.data.url, kind, content_type: r.data.content_type },
        ]);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
      if (cameraRef.current) cameraRef.current.value = "";
    }
  };

  const remove = (idx) => setMedia((prev) => prev.filter((_, i) => i !== idx));

  return (
    <div>
      {media.length > 0 && (
        <div className={`grid gap-2 mb-3 ${media.length === 1 ? "grid-cols-1" : "grid-cols-2"}`} data-testid={`${testIdPrefix}-media-grid`}>
          {media.map((m, i) => (
            <div key={m.file_id} className="relative rounded-xl overflow-hidden border border-white/10 bg-black/30">
              {m.kind === "video" ? (
                <video src={m.url} className="w-full h-40 object-cover" muted playsInline preload="metadata" />
              ) : (
                <img src={m.url} alt="" className="w-full h-40 object-cover" />
              )}
              <button
                type="button"
                data-testid={`${testIdPrefix}-remove-media-${i}`}
                onClick={() => remove(i)}
                className="absolute top-1.5 right-1.5 h-8 w-8 rounded-full bg-black/70 backdrop-blur border border-white/20 grid place-items-center text-white hover:bg-black/90"
                aria-label="Remove media"
              >
                <X className="h-4 w-4" />
              </button>
              {m.kind === "video" && (
                <div className="absolute bottom-1.5 left-1.5 px-2 py-0.5 rounded-full bg-black/70 border border-white/20 mono text-[10px] text-white flex items-center gap-1">
                  <Film className="h-3 w-3" /> VIDEO
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {canAdd && (
        <div className="flex flex-wrap gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/*,video/*"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid={`${testIdPrefix}-file-input`}
          />
          <input
            ref={cameraRef}
            type="file"
            accept="image/*,video/*"
            capture="environment"
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid={`${testIdPrefix}-camera-input`}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid={`${testIdPrefix}-attach-media`}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-white/[0.04] border border-white/10 text-white/80 text-sm hover:bg-white/[0.08] hover:text-white transition-colors min-h-[44px]"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
            {uploading ? "Uploading…" : "Photo / Video"}
          </button>
          <button
            type="button"
            onClick={() => cameraRef.current?.click()}
            disabled={uploading}
            data-testid={`${testIdPrefix}-open-camera`}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-white/[0.04] border border-white/10 text-white/80 text-sm hover:bg-white/[0.08] hover:text-white transition-colors min-h-[44px]"
          >
            <Camera className="h-4 w-4" /> Camera
          </button>
        </div>
      )}
    </div>
  );
}
